import { useSelect, useMultipleSelection } from 'downshift';
import * as React from 'react';
import { useRef } from 'react';
import { DropdownOption } from '@tcl-argon-models/models';
import { usePopper } from 'react-popper';

import { Label } from './Label';
import { Input, InputParentRef, useInputRef } from './Input';
import { Menu } from './Menu';
import { SubContent } from './SubContent';
import {
  handleSelectItem,
  offsetModifier,
  reorderList,
  sameWidthModifierCreator,
  getStrategy,
  getNewDropdownOptionsArray,
  handleArrowKeyComboBox,
} from './coreUtils';
import { useVirtual } from 'react-virtual';

interface DropdownCoreProps {
  items: Array<DropdownOption>;
  id: string;
  classes: Record<string, string>;
  onChange: (selectedItems: DropdownOption[]) => void;
  label: string;
  hideLabel: boolean;
  disabled: boolean;
  tabIndex: number;
  selectedItems: Array<DropdownOption>;
  placeholder: string;
  shouldShowClearbutton: boolean;
  clearable: boolean;
  subContent: React.ReactNode;
  isSingleSelect: boolean;
  selectedItemsTextOverride?: string;
  menuWidth?: string;
  escapeClippingArea: boolean;
}
export const DropdownCore: React.FC<DropdownCoreProps> = ({
  items,
  id,
  classes,
  onChange,
  label,
  hideLabel,
  disabled,
  selectedItems,
  tabIndex,
  placeholder,
  shouldShowClearbutton,
  clearable,
  subContent,
  isSingleSelect,
  selectedItemsTextOverride,
  menuWidth,
  escapeClippingArea,
  ...props
}) => {
  const inputParentRef: React.MutableRefObject<InputParentRef> = useInputRef();
  const popperRef = useRef(null);

  // Popper expects modifiers to be constant objects, so we memoize the object
  // creation to prevent recreation on rerender.
  const sameWidthModifier = React.useMemo(() => sameWidthModifierCreator({ menuWidth }), [menuWidth]);

  const { styles, attributes } = usePopper(inputParentRef.current.inputWrapper, popperRef.current, {
    placement: 'bottom-start',
    modifiers: [sameWidthModifier, offsetModifier],
    strategy: getStrategy(escapeClippingArea),
  });

  const {
    removeSelectedItem,
    getDropdownProps,
    addSelectedItem,
    setSelectedItems,
    selectedItems: selectedItems_,
  } = useMultipleSelection({ initialSelectedItems: [], selectedItems });

  const getOrderedList = () => reorderList(items, selectedItems);

  const dropdownOptions = React.useMemo(
    () =>
      selectedItems.length
        ? isSingleSelect && clearable
          ? [{ label: 'None', value: 'clear' }].concat(items)
          : isSingleSelect && !clearable
          ? items
          : getNewDropdownOptionsArray(selectedItems, items)
        : isSingleSelect
        ? items
        : getOrderedList(),
    [items, selectedItems, isSingleSelect],
  );

  const rowVirtualizer = useVirtual({
    size: dropdownOptions.length,
    parentRef: popperRef,
  });

  const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    id,
    selectedItem: null,
    items: dropdownOptions,
    scrollIntoView: () => {},
    onHighlightedIndexChange: ({ highlightedIndex }) =>
      highlightedIndex === -1 ? '' : rowVirtualizer.scrollToIndex(highlightedIndex),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: !isSingleSelect,
          };
        case useSelect.stateChangeTypes.ToggleButtonClick:
          return {
            ...changes,
            isOpen: true,
          };
        case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
          return handleArrowKeyComboBox({
            changes,
            dropdownOptions,
            directionDown: true,
            clearable,
            defaultOptionsLabel: '',
          });
        case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
          return handleArrowKeyComboBox({
            changes,
            dropdownOptions,
            directionDown: false,
            clearable,
            defaultOptionsLabel: '',
          });
      }
      return changes;
    },
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (selectedItem !== null && selectedItem !== undefined) {
            if (selectedItem.value === 'clear') {
              onChange([]);
              setSelectedItems([]);
            } else {
              handleSelectItem({
                isSingleSelect,
                newlySelectedItem: selectedItem,
                alreadySelectedItems: selectedItems_,
                onChange,
                addSelectedItem,
                removeSelectedItem,
                setSelectedItems,
              });
            }
          }
          break;
        default:
          break;
      }
    },
  });
  return (
    <div
      className={classes.root}
      data-analytics={isSingleSelect ? 'single-select-dropdown' : 'multi-select-dropdown'}
      {...props}
    >
      <Label {...{ label, classes, hideLabel, id, getLabelProps }} />
      <Input
        {...{
          ref: inputParentRef,
          classes,
          disabled,
          tabIndex,
          getToggleButtonProps,
          selectedItems: selectedItems_,
          placeholder,
          clearInput: () => {},
          shouldShowClearbutton,
          isOpen,
          isComboBox: false,
          isTypeahead: false,
          getDropdownProps,
          isSingleSelect,
          selectedItemsTextOverride,
          handleKeyPress: () => {},
        }}
      >
        <Menu
          {...{
            ref: popperRef,
            classes,
            isOpen,
            styles,
            attributes,
            getItemProps,
            highlightedIndex,
            items: dropdownOptions,
            getMenuProps,
            isSingleSelect,
            selectedItems,
            isTypeahead: false,
            clearable,
            rowVirtualizer,
          }}
        />
      </Input>
      <SubContent {...{ subContent, classes }} />
    </div>
  );
};
